export enum PollyEngine {
  STANDARD = 'standard',
  NEURAL = 'neural',
  LONG_FORM = 'long-form',
  GENERATIVE = 'generative',
}

export enum PollyLanguage {
  ARABIC = 'ar',
  CHINESE = 'zh',
  CZECH = 'cs',
  DANISH = 'da',
  DUTCH = 'nl',
  ENGLISH = 'en',
  FINNISH = 'fi',
  FRENCH = 'fr',
  GERMAN = 'de',
  HINDI = 'hi',
  ICELANDIC = 'is',
  ITALIAN = 'it',
  JAPANESE = 'ja',
  KOREAN = 'ko',
  NORWEGIAN = 'no',
  POLISH = 'pl',
  PORTUGUESE = 'pt',
  ROMANIAN = 'ro',
  RUSSIAN = 'ru',
  SPANISH = 'es',
  SWEDISH = 'sv',
  TURKISH = 'tr',
  WELSH = 'cy',
}

export const DEFAULT_SCRIPT_LANGUAGES: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.PORTUGUESE,
  PollyLanguage.SPANISH,
];

// Only the best available engine per language (generative where supported, neural otherwise).
// Female voices only.
export const voicesByLanguage: Record<
  PollyLanguage,
  { engine: PollyEngine.GENERATIVE | PollyEngine.NEURAL; voices: string[] }
> = {
  [PollyLanguage.ARABIC]: { engine: PollyEngine.NEURAL, voices: ['Hala'] },
  [PollyLanguage.CHINESE]: { engine: PollyEngine.NEURAL, voices: ['Zhiyu'] },
  [PollyLanguage.CZECH]: { engine: PollyEngine.NEURAL, voices: ['Jitka'] },
  [PollyLanguage.DANISH]: { engine: PollyEngine.NEURAL, voices: ['Sofie'] },
  [PollyLanguage.DUTCH]: { engine: PollyEngine.NEURAL, voices: ['Laura'] },
  [PollyLanguage.ENGLISH]: {
    engine: PollyEngine.GENERATIVE,
    voices: [
      'Amy',
      'Ayanda',
      'Danielle',
      'Joanna',
      'Niamh',
      'Olivia',
      'Ruth',
      'Salli',
      'Vicki',
    ],
  },
  [PollyLanguage.FINNISH]: { engine: PollyEngine.NEURAL, voices: ['Suvi'] },
  [PollyLanguage.FRENCH]: { engine: PollyEngine.NEURAL, voices: ['Lea'] },
  [PollyLanguage.GERMAN]: { engine: PollyEngine.NEURAL, voices: ['Vicki'] },
  [PollyLanguage.HINDI]: { engine: PollyEngine.NEURAL, voices: ['Kajal'] },
  [PollyLanguage.ICELANDIC]: { engine: PollyEngine.NEURAL, voices: ['Dora'] },
  [PollyLanguage.ITALIAN]: { engine: PollyEngine.NEURAL, voices: ['Bianca'] },
  [PollyLanguage.JAPANESE]: {
    engine: PollyEngine.NEURAL,
    voices: ['Kazuha', 'Tomoko'],
  },
  [PollyLanguage.KOREAN]: { engine: PollyEngine.NEURAL, voices: ['Seoyeon'] },
  [PollyLanguage.NORWEGIAN]: { engine: PollyEngine.NEURAL, voices: ['Ida'] },
  [PollyLanguage.POLISH]: { engine: PollyEngine.NEURAL, voices: ['Ola'] },
  [PollyLanguage.PORTUGUESE]: {
    engine: PollyEngine.GENERATIVE,
    voices: ['Camila'],
  },
  [PollyLanguage.ROMANIAN]: { engine: PollyEngine.NEURAL, voices: ['Carmen'] },
  [PollyLanguage.RUSSIAN]: { engine: PollyEngine.NEURAL, voices: ['Tatyana'] },
  [PollyLanguage.SPANISH]: { engine: PollyEngine.GENERATIVE, voices: ['Lupe'] },
  [PollyLanguage.SWEDISH]: { engine: PollyEngine.NEURAL, voices: ['Elin'] },
  [PollyLanguage.TURKISH]: { engine: PollyEngine.NEURAL, voices: ['Burcu'] },
  [PollyLanguage.WELSH]: { engine: PollyEngine.NEURAL, voices: ['Gwyneth'] },
};

/**
 * Returns the best engine and first female voice for a language.
 * English always uses Ruth on generative.
 */
export const selectVoiceForLanguage = (
  language: PollyLanguage,
  voicesByLanguageFromEvent?: Partial<
    Record<PollyLanguage, { engine: string; voiceId: string }>
  >,
): { engine: PollyEngine; voiceId: string } => {
  const fromEvent = voicesByLanguageFromEvent?.[language];

  if (fromEvent?.engine && fromEvent?.voiceId) {
    return {
      engine: fromEvent.engine as PollyEngine,
      voiceId: fromEvent.voiceId,
    };
  }

  if (language === PollyLanguage.ENGLISH) {
    return { engine: PollyEngine.GENERATIVE, voiceId: 'Ruth' };
  }

  const entry = voicesByLanguage[language];
  if (entry?.voices[0]) {
    return { engine: entry.engine, voiceId: entry.voices[0] };
  }

  return { engine: PollyEngine.GENERATIVE, voiceId: 'Ruth' };
};
